/*
|--------------------------------------------------------------------------
| Component: MediaSelectorButton.jsx
| Author: Sayed Ahmed
| Description: A reusable media picker button that integrates with a modal
|              and supports both single and multiple image selections.
|--------------------------------------------------------------------------
*/

import { useState, useMemo, useEffect } from "react";
import MediaModal from "./MediaModal";
import getImageUrl from "../../../utils/getImageUrl";
import { useTranslation } from "react-i18next";

export default function MediaSelectorButton({
  label = "Select Media",
  required = false,
  selectionType = "single", // "single" or "multiple"
  inputName = "media",
  onChange = () => {},
  register,
  errors = {},
  defaultSelected = [], // return full object record upload of array
}) {
  const [showModal, setShowModal] = useState(false);
  const [selectedUploads, setSelectedUploads] = useState([]);
  const { t } = useTranslation();

  // Load default selections
  useEffect(() => {    
    if (defaultSelected.length > 0) {
      setSelectedUploads(defaultSelected);
    }
  }, [defaultSelected]);

  // Create a unique input ID to link to the fake file input
  const uniqueId = useMemo(() => `media-fake-input-${Math.random().toString(36).slice(2, 10)}`, []);

  // Handle saving images selected in the modal
  const handleSave = (selectedList) => {
    setSelectedUploads(selectedList);

    if (selectionType === "single") {
      const selectedId = selectedList[0]?.id || null;
      onChange(selectedId);
    } else {
      const selectedIds = selectedList.map((item) => item.id);
      onChange(selectedIds, selectedList);
    }

    setShowModal(false);
  };

  return (
    <div className="form-group mb-3">
      {/* Label */}
      {label && (
        <label htmlFor={inputName}>
          {t(label)} {required && <span className="text-danger">*</span>}
        </label>
      )}

      {/* Hidden input to trigger modal on click */}
      <input
        type="file"
        id={uniqueId}
        className="d-none"
        onClick={(e) => {
          e.preventDefault();
          setShowModal(true);
        }}
      />

      {/* Fake input that shows selected image count */}
      <label
        htmlFor={uniqueId}
        className="form-control mb-2"
        style={{
          cursor: "pointer",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {selectedUploads.length > 0
          ? selectionType === "single"
            ? "1 image selected"
            : `${selectedUploads.length} images selected`
          : t("Click to choose from Media")}
      </label>

      {/* Image preview grid */}
      <div
        className="preview-grid"
        data-preview-input={inputName}
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(80px, 1fr))",
          gap: "0.5rem",
          marginTop: 8,
        }}
      >
        {selectedUploads.map((upload, index) => (
          <div
            key={index}
            style={{
              width: "100%",
              aspectRatio: "1 / 1",
              overflow: "hidden",
            }}
          >
            <img
              src={getImageUrl(upload.path)}
              alt="preview"
              className="rounded shadow-sm"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          </div>
        ))}
      </div>

      {/* Hidden field for form submission */}
      <input
        type="hidden"
        {...(register
          ? register(inputName, {
              required: required ? t("Field is required") : false,
            })
          : {})}
        value={
          selectionType === "single"
            ? selectedUploads[0]?.id || ""
            : selectedUploads.map((u) => u.id)
        }
      />

      {/* Validation error message */}
      {errors?.[inputName]?.message && (
        <p className="text-danger small mt-1">{errors[inputName]?.message}</p>
      )}

      {/* Media selection modal */}
      <MediaModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onSave={handleSave}
        selectionType={selectionType}
        selectedImages={selectedUploads.map((u) => u.id)}
      />
    </div>
  );
}
